import {
  CONCESSION_GALLERY_VARIANTS,
  getConcessionGallerySectionFieldErrors,
  hasConcessionGallerySectionFieldErrors,
  normalizeConcessionGalleryItems,
  reconcileGalleryItemsForVariant
} from './concessionGalleryItemDefaults'

export const VENUE_RENTALS_HERO_DISPLAY_TYPES = {
  CAROUSEL: 'carousel',
  STATIC_IMAGE: 'static_image'
}

export const VENUE_RENTALS_HERO_DISPLAY_OPTIONS = [
  { label: 'Carousel (image or video)', value: VENUE_RENTALS_HERO_DISPLAY_TYPES.CAROUSEL },
  { label: 'Static Image', value: VENUE_RENTALS_HERO_DISPLAY_TYPES.STATIC_IMAGE }
]

export const getDefaultVenueRentalsHero = () => ({
  display_type: VENUE_RENTALS_HERO_DISPLAY_TYPES.STATIC_IMAGE,
  header: '',
  image: { url: '', alt: '' },
  cta: { label: '', url: '' },
  email: { icon_url: '', text: '', href: '' },
  gallery_items: reconcileGalleryItemsForVariant(
    [],
    CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL
  )
})

export const normalizeVenueRentalsHero = (hero = {}) => {
  const displayType = hero?.display_type === VENUE_RENTALS_HERO_DISPLAY_TYPES.CAROUSEL
    ? VENUE_RENTALS_HERO_DISPLAY_TYPES.CAROUSEL
    : VENUE_RENTALS_HERO_DISPLAY_TYPES.STATIC_IMAGE

  return {
    display_type: displayType,
    header: String(hero?.header ?? '').trim(),
    image: {
      url: String(hero?.image?.url ?? hero?.image?.src ?? '').trim(),
      alt: String(hero?.image?.alt ?? '').trim()
    },
    cta: {
      label: String(hero?.cta?.label ?? '').trim(),
      url: String(hero?.cta?.url ?? hero?.cta?.href ?? '').trim()
    },
    email: {
      icon_url: String(hero?.email?.icon_url ?? hero?.email?.icon ?? '').trim(),
      text: String(hero?.email?.text ?? '').trim(),
      href: String(hero?.email?.href ?? '').trim()
    },
    gallery_items: normalizeConcessionGalleryItems(
      reconcileGalleryItemsForVariant(
        hero?.gallery_items,
        CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL
      )
    )
  }
}

export const mergeVenueRentalsPageSections = (page = {}) => ({
  venue_rentals_hero: normalizeVenueRentalsHero({
    ...getDefaultVenueRentalsHero(),
    ...(page?.venue_rentals_hero || {})
  })
})

export const updateVenueRentalsSection = (page, sectionKey, partial) => ({
  ...page,
  [sectionKey]: {
    ...(page?.[sectionKey] || getDefaultVenueRentalsHero()),
    ...partial
  }
})

export const getVenueRentalsHeroFieldErrors = (hero = {}) => {
  const normalized = normalizeVenueRentalsHero(hero)

  if (normalized.display_type === VENUE_RENTALS_HERO_DISPLAY_TYPES.CAROUSEL) {
    const carouselErrors = getConcessionGallerySectionFieldErrors({
      enabled: true,
      title: '',
      type: CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL,
      galleryItems: normalized.gallery_items,
      requireSectionHeader: false
    })

    return {
      displayType: '',
      header: !normalized.header ? 'Hero header is required' : '',
      image: '',
      ctaLabel: '',
      ctaUrl: '',
      emailIcon: '',
      emailText: '',
      emailHref: '',
      carousel: carouselErrors
    }
  }

  return {
    displayType: '',
    header: !normalized.header ? 'Hero header is required' : '',
    image: !normalized.image.url ? 'Hero image is required' : '',
    ctaLabel: '',
    ctaUrl: '',
    emailIcon: '',
    emailText: '',
    emailHref: '',
    carousel: { title: '', cardCount: '', items: [] }
  }
}

export const hasVenueRentalsHeroFieldErrors = (fieldErrors = {}) => {
  if (fieldErrors.header || fieldErrors.image || fieldErrors.displayType) {
    return true
  }

  return hasConcessionGallerySectionFieldErrors(fieldErrors.carousel)
}

export const validateVenueRentalsHero = (hero = {}) => {
  const fieldErrors = getVenueRentalsHeroFieldErrors(hero)

  if (fieldErrors.header) {
    return { isValid: false, message: fieldErrors.header }
  }

  if (fieldErrors.image) {
    return { isValid: false, message: fieldErrors.image }
  }

  if (fieldErrors.carousel?.cardCount) {
    return { isValid: false, message: fieldErrors.carousel.cardCount }
  }

  const itemError = (fieldErrors.carousel?.items || []).find(item => (
    item?.src || item?.videoUrl
  ))

  if (itemError?.src) {
    return { isValid: false, message: itemError.src }
  }

  if (itemError?.videoUrl) {
    return { isValid: false, message: itemError.videoUrl }
  }

  return { isValid: true, message: '' }
}

export const getDefaultVenueRentalsCarouselItems = () => (
  reconcileGalleryItemsForVariant([], CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL)
)
